import React from "react";
import {
  Download,
  FileEdit,
  Clock,
  Search,
  CheckCircle,
  Radio,
  Wifi,
  ChevronDown,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface StatusTransition {
  status: string;
  label: string;
  action: string;
}

interface AssetVersion {
  id: string;
  status?: string;
  ad_title?: string | null;
  available_transitions?: StatusTransition[];
}

/** The transition a status button applies when clicked. */
interface TransitionAction {
  targetStatus: string;
  label: string;
  action: string;
}

interface AdBuilderStatusBarProps {
  activeVersion: AssetVersion | undefined;
  adTitle: string;
  onAdTitleChange: (value: string) => void;
  isTransitioning: boolean;
  /** Applies the transition immediately; there is no confirmation step. */
  onTransitionClick: (action: TransitionAction) => void;
  permissionError?: string | null;
  onDownload?: () => void;
  isDownloading?: boolean;
  /** Rendered after the download button, e.g. "Share preview". */
  renderShare?: (textColor: string) => React.ReactNode;
}

// Colour map per status (background hex)
const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  draft: { bg: "#b5b5b5", text: "#ffffff" },
  briefed: { bg: "#f5e130", text: "#5a4a00" },
  in_progress: { bg: "#f5a42a", text: "#ffffff" },
  in_review: { bg: "#d97474", text: "#ffffff" },
  approved: { bg: "#c6d450", text: "#3a4200" },
  trafficking: { bg: "#7b7fce", text: "#ffffff" },
  live: { bg: "#5bbfcc", text: "#ffffff" },
};

// Status label + icon
const STATUS_META: Record<string, { label: string; icon: React.ReactNode }> = {
  draft: { label: "Draft", icon: <FileEdit size={16} /> },
  briefed: { label: "Briefed", icon: <Clock size={16} /> },
  in_progress: { label: "In Progress", icon: <Clock size={16} /> },
  in_review: { label: "In Review", icon: <Search size={16} /> },
  approved: { label: "Approved", icon: <CheckCircle size={16} /> },
  trafficking: { label: "Trafficking", icon: <Radio size={16} /> },
  live: { label: "Live", icon: <Wifi size={16} /> },
};

const AdBuilderStatusBar: React.FC<AdBuilderStatusBarProps> = ({
  activeVersion,
  renderShare,
  adTitle,
  onAdTitleChange,
  isTransitioning,
  onTransitionClick,
  permissionError,
  onDownload,
  isDownloading,
}) => {
  const currentStatus = activeVersion?.status ?? "draft";
  const availableTransitions = activeVersion?.available_transitions ?? [];

  const style = STATUS_STYLES[currentStatus] ?? STATUS_STYLES.draft;
  const meta = STATUS_META[currentStatus] ?? STATUS_META.draft;
  
  // Primary action is the first available transition (typically the forward action)
  const primaryAction = availableTransitions[0];
  const hasMultipleActions = availableTransitions.length > 1;
  
  const showDownload = [
    "in_review",
    "approved",
    "trafficking",
    "live",
  ].includes(currentStatus);

  return (
    <>
      <div style={{ backgroundColor: style.bg }} className="px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Ad Title input */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span
              style={{ color: style.text }}
              className="font-medium whitespace-nowrap"
            >
              Ad Title
            </span>
            <input
              type="text"
              value={adTitle}
              onChange={(e) => onAdTitleChange(e.target.value)}
              style={{
                borderColor: "rgba(255,255,255,0.3)",
                color: style.text,
              }}
              className="flex-1 max-w-md px-4 py-2 rounded-lg border-2 bg-white/15 placeholder-white/50 focus:outline-none focus:border-white/60"
              placeholder="Enter ad title"
            />
          </div>

          {/* Status label + action buttons */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Status badge */}
            <div
              style={{ color: style.text }}
              className="flex items-center gap-1.5 font-semibold text-sm"
            >
              {meta.icon}
              <span>{meta.label}</span>
            </div>

            {/* Download button for applicable statuses */}
            {showDownload && (
              <button
                onClick={onDownload}
                disabled={isDownloading || !onDownload}
                style={{
                  borderColor: "rgba(255,255,255,0.6)",
                  color: style.text,
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <Download size={14} />
                {isDownloading ? "Downloading…" : "Download Ad"}
              </button>
            )}

            {renderShare?.(style.text)}

            {/* Status transition actions */}
            {availableTransitions.length > 0 && (
              <div className="flex items-center gap-2">
                {/* Primary action button */}
                {primaryAction && (
                  <button
                    disabled={isTransitioning}
                    onClick={() =>
                      onTransitionClick({
                        targetStatus: primaryAction.status,
                        label: primaryAction.label,
                        action: primaryAction.action,
                      })
                    }
                    className="px-4 py-2 rounded-full bg-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    style={{ color: style.bg }}
                  >
                    {isTransitioning && (
                      <Loader2 size={14} className="animate-spin" />
                    )}
                    {isTransitioning ? "Updating…" : primaryAction.action}
                  </button>
                )}

                {/* More actions dropdown */}
                {hasMultipleActions && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        disabled={isTransitioning}
                        className="px-3 py-2 rounded-full bg-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{ color: style.bg }}
                      >
                        <ChevronDown size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {availableTransitions.slice(1).map((transition) => (
                        <DropdownMenuItem
                          key={transition.status}
                          onClick={() =>
                            onTransitionClick({
                              targetStatus: transition.status,
                              label: transition.label,
                              action: transition.action,
                            })
                          }
                        >
                          {transition.action}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}
            
            {/* Show message if no transitions available */}
            {availableTransitions.length === 0 && currentStatus !== "live" && (
              <div
                style={{ color: style.text }}
                className="text-sm opacity-70 italic"
              >
                No available actions
              </div>
            )}
          </div>
        </div>

        {/* Permission errors used to appear inside the confirmation dialog.
            With transitions applied directly there is no dialog to host them,
            so they surface here instead of failing silently. */}
        {permissionError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 font-medium">
              {permissionError}
            </p>
          </div>
        )}
      </div>

    </>
  );
};

export default AdBuilderStatusBar;
